/**
 * The business homepage — v149.76.
 *
 * Four pillars: Smart Screens · Smart QR · Smart Web Link · Loyalty & Growth.
 * Fifteen section KEYS (frozen in `lib/landingSections.js`; the backend
 * whitelist + a test pin them) are mapped onto thirteen rendered sections —
 * `biz_nearby` and `biz_pricing` merged into neighbours and render nothing.
 * `Landing.jsx` renders them in that order and swaps its header for
 * `BusinessNav` whenever any of them is on.
 *
 * Styling: the scoped homepage theme in `styles/landing-v3.css`
 * (`[data-landing="v3"]`, set by Landing.jsx). Colours come ONLY from the
 * `lv-*` classes and `--lv-*` variables; Tailwind is used for layout.
 *
 * Every number in a dashboard, list or KPI here is ILLUSTRATIVE and says
 * so on screen — none of it is Linkola customer data. No prices are
 * hard-coded: the Pricing section reads `GET /plans`.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, BadgeCheck, BarChart3, Building2, Check, ChevronRight, Coins, ExternalLink, Film, Gift,
  Globe, Heart, Image as ImageIcon, Info, Instagram, Link2, Mail, MapPin, Megaphone, Menu, MessageCircle, MonitorPlay,
  MoreHorizontal, Navigation, QrCode, Repeat, Send, Share2, ShoppingBag, Star, Store, Tag, Users, X,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import "../../styles/landing-v3.css";
import LinkolaMark from "../LinkolaMark";
import { api } from "../../lib/api";

const SCREENS_URL = "https://linkola.au";
// v149.76 — The QR codes on the page are REAL and scannable: they open a
// Linkola demo Web Link, so a visitor who scans the homepage sees the thing.
const DEMO_LINK_URL = "https://linkola.app/u/demo-brew-bite";

/** A genuine QR (finder patterns and all), scaled by its container. */
function RealQr({ className = "", fg = "#0B1220", style }) {
  return (
    <span className={className} style={style} aria-hidden>
      <QRCodeSVG value={DEMO_LINK_URL} size={256} level="M" fgColor={fg} bgColor="#FFFFFF" includeMargin={false}
                 style={{ width: "100%", height: "auto", display: "block" }} />
    </span>
  );
}

/** The glowing acrylic table stand ("SCAN HERE") seen on café counters. */
function QrStand({ title = "Scan here", sub = "View menu & order", mini = false, style }) {
  return (
    <div className={`lv-acrylic ${mini ? "lv-acrylic--mini" : ""}`} style={style} aria-hidden>
      <div className="lv-acrylic__face">
        <p className="lv-acrylic__title">{title}</p>
        <RealQr className="lv-acrylic__qr" />
        <p className="lv-acrylic__sub">{sub}</p>
        {!mini && <p className="lv-acrylic__brand">Powered by Linkola</p>}
      </div>
      <div className="lv-acrylic__base" />
    </div>
  );
}

/** A café: dark room, warm lights, a wooden table — the stand sits on it. */
function CafeScene({ children, neon = "Espresso bar", label }) {
  return (
    <div className="lv-scene" role="img" aria-label={label}>
      <div className="lv-scene__lights" aria-hidden />
      <span className="lv-scene__neon" aria-hidden>{neon}</span>
      <div className="lv-scene__table" aria-hidden />
      <div className="lv-scene__content">{children}</div>
    </div>
  );
}
// `.lv-section--dark .lv-icon` out-specifies `.lv-icon--green`, so a green
// icon tile inside a dark section is painted inline from the same tokens.
const GREEN_ICON = { background: "var(--lv-green)", color: "var(--lv-navy)" };

export function scrollToSection(id) {
  const el = typeof document !== "undefined" ? document.getElementById(id) : null;
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  return !!el;
}

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------
function Section({ id, testid, tone = "light", className = "", children }) {
  return (
    <section id={id} data-testid={testid} className={`lv-section lv-section--${tone} scroll-mt-20 overflow-hidden ${className}`}>
      <div className="lv-container">{children}</div>
    </section>
  );
}

function Head({ eyebrow, title, lead, center = false, className = "" }) {
  return (
    <div className={`min-w-0 ${center ? "text-center mx-auto max-w-3xl" : "max-w-2xl"} ${className}`}>
      {eyebrow && <p className="lv-eyebrow mb-5">{eyebrow}</p>}
      <h2 className="lv-h2">{title}</h2>
      {lead && <p className={`lv-lead mt-5 ${center ? "mx-auto" : ""}`}>{lead}</p>}
    </div>
  );
}

function DemoBadge({ testid }) {
  return <span className="lv-demo-badge" data-testid={testid}>Illustrative demo data</span>;
}

function Points({ items, green = false }) {
  return (
    <ul className="grid gap-3">
      {items.map((p) => {
        const Icon = p.icon || Check;
        return (
          <li key={p.text} className="flex items-start gap-3 min-w-0">
            <span className="lv-icon" style={{ width: 36, height: 36, borderRadius: 10, ...(green ? GREEN_ICON : {}) }}>
              <Icon className="w-4 h-4" strokeWidth={2.4} aria-hidden />
            </span>
            <span className="min-w-0 break-words pt-1.5 font-semibold text-[15px] leading-snug">{p.text}</span>
          </li>
        );
      })}
    </ul>
  );
}

function Terms({ testid = "biz-terms-link" }) {
  return (
    <p className="lv-body" style={{ fontSize: 12 }}>
      *Terms &amp; conditions apply.{" "}
      <Link to="/terms" className="underline underline-offset-2" data-testid={testid}>See terms</Link>
    </p>
  );
}

/** A Linktree-style Smart Web Link on a phone: cover, avatar, name, bio,
 *  socials, then the action buttons (`rows`; `hot` = dark, `tone` = green/coral). */
function PhoneList({ rows, title, compact = false, label, handle = "@cafearoma · Nellore", bio = "Specialty coffee · Open 8am–10pm" }) {
  return (
    <div className="lv-phone" role="img" aria-label={label}>
      <div className="lv-phone__notch" style={{ height: "7cqw", top: "4.5cqw", width: "34%" }} aria-hidden />
      <div className="lv-phone__screen" aria-hidden>
        <div className={`lv-lt ${compact ? "lv-lt--compact" : ""}`}>
          <div className="lv-lt__body">
            <span className="lv-lt__avatar">CA</span>
            <span className="lv-lt__name">{title} <BadgeCheck /></span>
            {!compact && <span className="lv-lt__handle">{handle}</span>}
            {!compact && <span className="lv-lt__bio">{bio}</span>}
            <span className="lv-lt__socials">
              <span><Instagram /></span>
              <span><MessageCircle /></span>
              <span><Globe /></span>
            </span>
            <span className="lv-lt__btns">
              {rows.map((r) => {
                const Icon = r.icon;
                const tone = r.tone ? `lv-lt__btn--${r.tone}` : r.hot ? "lv-lt__btn--hot" : "";
                return (
                  <span key={r.label} className={`lv-lt__btn ${tone}`}>
                    <Icon aria-hidden />
                    <span>{r.label}</span>
                    <ChevronRight style={{ opacity: 0.6 }} aria-hidden />
                  </span>
                );
              })}
            </span>
            <span className="lv-lt__foot">Powered by Linkola</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** The screen creative used by the hero and the Smart Screens section. */
function AdCreative({ glow = true }) {
  return (
    <div className="lv-screen" aria-hidden>
      <div className="lv-screen__panel">
        {glow && <div className="lv-screen__glow" />}
        <div className="relative h-full p-[6%] flex flex-col justify-between">
          <span className="lv-chip lv-chip--green self-start" style={{ fontSize: 10, padding: "3px 9px" }}>Limited edition</span>
          <div className="min-w-0">
            <p className="lv-display" style={{ fontSize: "clamp(1rem, 4.4vw, 1.9rem)", color: "var(--lv-surface)" }}>
              TRY OUR NEW<br /><span className="lv-accent-green">MATCHA LATTE</span>
            </p>
            <p className="lv-mono mt-2" style={{ color: "var(--lv-slate)" }}>This week · Café Aroma</p>
          </div>
          <div className="flex items-end justify-between gap-2">
            <span className="lv-mono" style={{ color: "var(--lv-slate)" }}>Scan for the offer</span>
            <RealQr className="block" style={{ width: "16%" }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header for the business homepage
// ---------------------------------------------------------------------------
const NAV_LINKS = [
  { id: "smart-screens", label: "Smart Screens", key: "biz_journey" },
  { id: "smart-qr", label: "Smart QR", key: "biz_actions" },
  { id: "smart-link", label: "Smart Web Link", key: "biz_multi_branch" },
  { id: "for-business", label: "For Business", key: "biz_branch_compare" },
  { id: "pricing", label: "Pricing", key: "biz_plans" },
];

export function BusinessNav({ sections, user, onGetLinkola, onLogin, onOpenApp, onPartner }) {
  const [open, setOpen] = useState(false);
  const links = NAV_LINKS.filter((l) => sections[l.key]);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);
  const go = (id) => { setOpen(false); scrollToSection(id); };
  const menuItem = "lv-btn lv-btn--ghost-dark w-full min-h-[44px] text-[15px]";

  return (
    <div className="relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-1.5 sm:gap-4 min-w-0">
        <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
           className="flex items-center gap-2 flex-shrink-0 min-h-[44px]" data-testid="biz-nav-home" aria-label="Linkola home">
          <LinkolaMark size={32} />
          <span className="hidden sm:inline lv-display text-xl">Linkola</span>
        </a>
        <nav className="hidden lg:flex items-center gap-5 ml-4" data-testid="biz-nav" aria-label="Homepage sections">
          {links.map((l) => (
            <button key={l.id} type="button" onClick={() => go(l.id)} data-testid={`biz-nav-${l.id}`}
                    className="min-h-[44px] text-[14px] font-semibold hover:underline underline-offset-4 whitespace-nowrap"
                    style={{ color: "var(--lv-slate)" }}>
              {l.label}
            </button>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          {user ? (
            <button type="button" onClick={onOpenApp} data-testid="nav-go-app-btn" className="lv-btn lv-btn--primary lv-btn--sm">
              Open app <ArrowUpRight className="w-4 h-4" aria-hidden />
            </button>
          ) : (
            <>
              <button type="button" onClick={onLogin} data-testid="nav-signin-btn" className="lv-btn lv-btn--ghost-dark lv-btn--sm" style={{ padding: "0 12px" }}>
                Login
              </button>
              <button type="button" onClick={onGetLinkola} data-testid="biz-nav-get-linkola" className="lv-btn lv-btn--primary lv-btn--sm" style={{ padding: "0 12px" }}>
                Get Started
              </button>
            </>
          )}
          <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls="biz-nav-menu"
                  aria-label={open ? "Close menu" : "Open menu"} data-testid="biz-nav-menu-btn"
                  className="lv-btn lv-btn--ghost-dark lv-btn--sm flex-shrink-0" style={{ width: 44, height: 44, padding: 0 }}>
            {open ? <X className="w-5 h-5" aria-hidden />
              : <><Menu className="w-5 h-5 lg:hidden" aria-hidden /><MoreHorizontal className="w-5 h-5 hidden lg:block" aria-hidden /></>}
          </button>
        </div>
      </div>
      {open && (
        <div id="biz-nav-menu" data-testid="biz-nav-menu"
             className="absolute inset-x-0 top-full z-40 max-h-[calc(100dvh-4.5rem)] overflow-y-auto overscroll-contain"
             style={{ background: "var(--lv-navy-2)", color: "var(--lv-surface)", borderTop: "1px solid var(--lv-border-dark)",
                      borderBottom: "1px solid var(--lv-border-dark)", boxShadow: "var(--lv-shadow-lg)",
                      paddingLeft: "env(safe-area-inset-left, 0px)", paddingRight: "env(safe-area-inset-right, 0px)",
                      paddingBottom: "env(safe-area-inset-bottom, 0px)" }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {links.map((l) => (
              <button key={l.id} type="button" onClick={() => go(l.id)} data-testid={`biz-menu-${l.id}`} className={`${menuItem} lg:hidden`}>
                {l.label}
              </button>
            ))}
            <Link to="/for/personal" onClick={() => setOpen(false)} data-testid="biz-menu-personal" className={menuItem}>Personal pages</Link>
            <Link to="/for/creators" onClick={() => setOpen(false)} data-testid="biz-menu-creators" className={menuItem}>For creators</Link>
            {!user && (
              <button type="button" onClick={() => { setOpen(false); onPartner(); }} data-testid="biz-menu-partner" className={menuItem}>
                Become a Channel Partner
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1 · Hero (biz_hero)
// ---------------------------------------------------------------------------
const PILLARS = [
  { id: "smart-screens", icon: MonitorPlay, title: "Smart Screens", text: "Promote on digital screens inside nearby businesses." },
  { id: "smart-qr", icon: QrCode, title: "Smart QR", text: "Turn every scan into a customer connection." },
  { id: "smart-link", icon: Link2, title: "Smart Web Link", text: "One link for everything about your business." },
  { id: "loyalty", icon: Repeat, title: "Loyalty & Growth", text: "Reward repeat visits and stay remembered." },
];
const HERO_PHONE = [
  { icon: MessageCircle, label: "WhatsApp", hot: true }, { icon: Tag, label: "Offers" },
  { icon: Star, label: "Reviews" }, { icon: Navigation, label: "Directions" },
];

function Hero({ user, onGetLinkola, onOpenApp }) {
  return (
    <section id="top" data-testid="biz-hero" className="lv-section lv-section--dark lv-grid-bg scroll-mt-20 overflow-hidden relative">
      <div className="lv-glow lv-glow--green" style={{ width: 420, height: 420, top: -120, left: -120 }} aria-hidden />
      <div className="lv-glow lv-glow--coral" style={{ width: 360, height: 360, bottom: -140, right: -80 }} aria-hidden />
      <div className="lv-container relative">
        <div className="lv-split">
          <div className="min-w-0 lv-fade-up">
            <p className="lv-eyebrow mb-6">One platform. Multiple ways to grow.</p>
            <h1 className="lv-h1" data-testid="biz-hero-headline">
              Connect More Customers.<br /><span className="lv-accent-green">Grow Your Business.</span>
            </h1>
            <p className="lv-lead mt-6" data-testid="biz-hero-sub">
              From Smart Screens and QR promotions to one powerful business link, loyalty rewards and customer
              engagement — Linkola helps businesses get discovered, connect with customers and bring them back.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button type="button" className="lv-btn lv-btn--primary lv-btn--lg" onClick={user ? onOpenApp : onGetLinkola} data-testid="biz-hero-cta">
                {user ? "Open app" : "Get Started"} <ArrowUpRight className="w-5 h-5" aria-hidden />
              </button>
              <button type="button" className="lv-btn lv-btn--ghost-dark lv-btn--lg" onClick={() => scrollToSection("capabilities")} data-testid="biz-hero-explore">
                Explore Linkola
              </button>
            </div>
          </div>
          {/* One composed ecosystem visual: realistic shop photo
              backdrop with the live mock widgets layered on top so the
              hero reads like "Linkola runs inside real venues". The
              photo + glow sit behind the composition; the brutal
              shadowed widgets (screen, phone, QR stand) stay perfectly
              intact above. v149.80. */}
          <div className="relative w-full max-w-[560px] mx-auto px-6 sm:px-10 pt-2 pb-20 min-w-0" role="img"
               aria-label="A Smart Screen showing an offer, a phone showing a Smart Web Link and a Smart QR stand running inside a real shop" data-testid="biz-hero-visual">
            <span aria-hidden className="absolute inset-x-2 sm:inset-x-6 top-0 bottom-16 rounded-3xl overflow-hidden pointer-events-none border-2 border-ink"
                  style={{ boxShadow: "0 20px 60px -20px rgba(16,185,129,0.5), 0 10px 30px -10px rgba(167,139,250,0.4)", zIndex: 0 }}>
              <img src="/landing/hero-shop-screen.webp"
                   alt=""
                   loading="eager"
                   className="absolute inset-0 w-full h-full object-cover" />
              <span aria-hidden className="absolute inset-0"
                    style={{ background: "linear-gradient(180deg, rgba(10,20,30,0.0) 0%, rgba(10,20,30,0.1) 55%, rgba(10,20,30,0.55) 100%)" }} />
            </span>
            <AdCreative />
            <div className="lv-screen__stand" aria-hidden />
            <div className="absolute left-0 bottom-0" style={{ width: "28%", maxWidth: 140, minWidth: 92 }} aria-hidden>
              <PhoneList compact title="Café Aroma" rows={HERO_PHONE} />
            </div>
            <div className="absolute right-0 bottom-2" style={{ width: "26%", maxWidth: 128, minWidth: 84 }} aria-hidden>
              <QrStand mini title="Scan here" sub="Menu & offers" />
            </div>
          </div>
        </div>
        <ul className="lv-grid-4 mt-14" data-testid="biz-hero-capabilities">
          {PILLARS.map((p) => {
            const Icon = p.icon;
            return (
              <li key={p.id} className="lv-card lv-card--dark flex items-center gap-3 min-w-0" style={{ padding: "0.9rem 1rem" }}>
                <span className="lv-icon" style={{ width: 38, height: 38, ...GREEN_ICON }}><Icon className="w-5 h-5" aria-hidden /></span>
                <span className="min-w-0 break-words font-bold text-[14px] sm:text-[15px] leading-tight">{p.title}</span>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 2 · Four capabilities (biz_outcomes)
// ---------------------------------------------------------------------------
function Capabilities() {
  return (
    <Section id="capabilities" testid="biz-capabilities" tone="light">
      <Head center eyebrow="What Linkola does" title="Everything your business needs to be found, remembered and revisited." />
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mt-12">
        {PILLARS.map((p) => {
          const Icon = p.icon;
          return (
            <li key={p.id} className="lv-card lv-card--raised flex flex-col min-w-0" data-testid={`biz-cap-${p.id}`}>
              <span className="lv-icon lv-icon--navy"><Icon className="w-5 h-5" aria-hidden /></span>
              <h3 className="lv-h3 mt-5">{p.title}</h3>
              <p className="lv-body mt-2 flex-1">{p.text}</p>
              <button type="button" className="lv-link mt-5 inline-flex items-center gap-1 self-start min-h-[44px]"
                      onClick={() => scrollToSection(p.id)} data-testid={`biz-cap-${p.id}-link`}>
                Explore <ArrowRight className="w-4 h-4" aria-hidden />
              </button>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 3 · Smart Screens (biz_journey)
// ---------------------------------------------------------------------------
const NEARBY_SCREENS = [
  ["Café Aroma", "Café", "350 m"], ["FitZone Gym", "Fitness", "800 m"], ["City Pharmacy", "Pharmacy", "1.2 km"],
];
function SmartScreens() {
  return (
    // `--dark-2` for the navy-2 canvas; `--dark` (tone) so the dark text,
    // eyebrow, button and badge rules in landing-v3.css apply here too.
    <Section id="smart-screens" testid="biz-smart-screens" tone="dark" className="lv-section--dark-2">
      <div className="lv-split">
        <div className="min-w-0">
          <Head eyebrow="Smart Screens" title="Your Business. On Screens Around You."
                lead="Promote your products, services and offers on digital advertising screens inside nearby participating businesses. Reach local customers where they already shop." />
          <div className="mt-8">
            <Points green items={[
              { icon: MapPin, text: "Discover nearby advertising screens" },
              { icon: ImageIcon, text: "Promote with images or videos" },
              { icon: Users, text: "Reach customers in real-world locations" },
            ]} />
          </div>
          <p className="lv-body mt-6">Creatives are reviewed and published to the screens by the Linkola team.</p>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
            <a href={SCREENS_URL} target="_blank" rel="noopener noreferrer" className="lv-btn lv-btn--green lv-btn--lg" data-testid="biz-screens-cta">
              Explore Smart Screens <ExternalLink className="w-4 h-4" aria-hidden />
            </a>
          </div>
          <p className="lv-body mt-4">
            Have space for a screen?{" "}
            <a href={SCREENS_URL} target="_blank" rel="noopener noreferrer" className="lv-link" data-testid="biz-screens-host-link">
              Explore hosting opportunities.
            </a>
          </p>
        </div>
        <div className="min-w-0">
          {/* v149.80 — Realistic reception-screen photo backdrop with
              the mock AdCreative layered on top so visitors see what a
              Linkola screen actually looks like installed on a wall. */}
          <div className="relative lv-card lv-card--dark p-4 sm:p-6 overflow-hidden" style={{ background: "var(--lv-navy-3)", borderRadius: "var(--lv-radius-xl)" }}
               role="img" aria-label="A Smart Screen on a reception wall showing an offer creative" data-testid="biz-screens-visual">
            <span aria-hidden className="absolute inset-0 opacity-70 pointer-events-none">
              <img src="/landing/reception-screen.webp"
                   alt=""
                   loading="lazy"
                   className="absolute inset-0 w-full h-full object-cover" />
              <span className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(10,20,30,0.4) 0%, rgba(10,20,30,0.75) 100%)" }} />
            </span>
            <span aria-hidden className="absolute -top-10 -right-10 w-48 h-48 rounded-full blur-3xl opacity-50 pointer-events-none"
                  style={{ background: "radial-gradient(circle, rgba(16,185,129,0.6), transparent 70%)" }} />
            <div className="relative">
              <AdCreative />
              <div className="lv-screen__stand" aria-hidden />
              <div className="mt-4 h-5 rounded-t-xl" style={{ background: "var(--lv-navy-2)", borderTop: "1px solid var(--lv-border-dark)" }} aria-hidden />
            </div>
          </div>
          <div className="lv-card lv-card--dark mt-4" style={{ background: "var(--lv-navy)" }} data-testid="biz-screens-nearby">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <p className="font-bold text-[15px]">Nearby screens</p>
              <span className="ml-auto"><DemoBadge testid="biz-screens-demo" /></span>
            </div>
            <ul className="grid gap-2">
              {NEARBY_SCREENS.map(([venue, cat, dist]) => (
                <li key={venue} className="flex items-center gap-3 min-w-0 rounded-xl px-3 py-2.5" style={{ background: "var(--lv-navy-3)" }}>
                  <span className="lv-icon" style={{ width: 34, height: 34, background: "var(--lv-navy-2)", color: "var(--lv-green)" }}><MonitorPlay className="w-4 h-4" aria-hidden /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-[14px] truncate">{venue}</span>
                    <span className="block lv-mono" style={{ color: "var(--lv-slate)", fontSize: 10 }}>{cat} · {dist}</span>
                  </span>
                  <span className="lv-chip lv-chip--green" style={{ fontSize: 11, padding: "4px 9px" }}>Available</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 4 · Smart QR (biz_actions)
// ---------------------------------------------------------------------------
const QR_USES = [
  { icon: Tag, label: "Offers & coupons" }, { icon: Coins, label: "Loyalty rewards" }, { icon: Star, label: "Google Reviews" },
  { icon: MessageCircle, label: "WhatsApp enquiries" }, { icon: Info, label: "Business information" }, { icon: MapPin, label: "Nearby promotions" },
];
const QR_PHONE = [
  { icon: Tag, label: "Today's offer · 20% off", tone: "green" }, { icon: Coins, label: "Earn loyalty coins", tone: "coral" },
  { icon: Star, label: "Review us on Google" }, { icon: MessageCircle, label: "Chat on WhatsApp" },
];
function SmartQr({ onGetLinkola }) {
  return (
    <Section id="smart-qr" testid="biz-smart-qr" tone="light">
      <div className="lv-split lv-split--flip">
        <div className="min-w-0">
          <Head eyebrow="Smart QR" title="One Smart QR. Endless Opportunities."
                lead="A Smart QR connects physical customer interactions with digital business experiences. Every scan can become a customer connection." />
          <ul className="grid grid-cols-2 gap-2 mt-8" data-testid="biz-qr-uses">
            {QR_USES.map((u) => {
              const Icon = u.icon;
              return (
                <li key={u.label} className="lv-card flex items-center gap-2.5 min-w-0" style={{ padding: "0.75rem 0.9rem", boxShadow: "none" }}>
                  <Icon className="w-4 h-4 flex-shrink-0 lv-accent" aria-hidden />
                  <span className="min-w-0 break-words font-semibold text-[13px] sm:text-[14px] leading-tight">{u.label}</span>
                </li>
              );
            })}
          </ul>
          <div className="mt-8">
            <button type="button" className="lv-btn lv-btn--primary lv-btn--lg" onClick={onGetLinkola} data-testid="biz-qr-cta">
              Get a Smart QR <ArrowUpRight className="w-5 h-5" aria-hidden />
            </button>
          </div>
        </div>
        <div className="relative min-w-0" data-testid="biz-qr-visual">
          {/* v149.80 — Realistic café-counter photo backdrop behind
              the Smart QR stand + phone composition so the "scan on the
              counter" story reads instantly. Decorative only. */}
          <span aria-hidden className="absolute inset-x-0 top-6 bottom-10 rounded-3xl overflow-hidden pointer-events-none border-2 border-ink"
                style={{ zIndex: 0, boxShadow: "0 20px 60px -20px rgba(16,185,129,0.4), 0 10px 30px -10px rgba(167,139,250,0.3)" }}>
            <img src="/landing/smartqr-weblink.webp" alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-90" />
            <span className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.0) 0%, rgba(255,255,255,0.0) 55%, rgba(255,255,255,0.4) 100%)" }} />
          </span>
          <div className="relative">
            <CafeScene neon="Café Aroma" label="A café counter with a glowing Smart QR stand — Scan here, view menu and order — next to a coffee cup, and a phone showing the scanned experience: today's offer, earn loyalty coins, review us on Google, chat on WhatsApp">
              <div className="lv-cup hidden sm:block" aria-hidden />
              <QrStand title="Scan here" sub="View menu & order" style={{ width: "42%", maxWidth: 230, minWidth: 112 }} />
              <div style={{ width: "36%", maxWidth: 190, minWidth: 100, marginBottom: "-4%" }}>
                <PhoneList title="Café Aroma" rows={QR_PHONE} label="" />
              </div>
            </CafeScene>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 5 · Smart Web Link (biz_multi_branch)
// ---------------------------------------------------------------------------
const LINK_ROWS = [
  { icon: MessageCircle, label: "Chat on WhatsApp", tone: "green" }, { icon: ShoppingBag, label: "Products & catalogue" },
  { icon: Tag, label: "Today's offers", tone: "coral" }, { icon: Star, label: "Google Reviews" }, { icon: Navigation, label: "Directions" },
  { icon: Share2, label: "Social media", tone: "dark" }, { icon: Film, label: "Videos", tone: "dark" }, { icon: Mail, label: "Enquiries", tone: "dark" },
];
function SmartLink({ onGetLinkola }) {
  return (
    <Section id="smart-link" testid="biz-smart-link" tone="soft">
      <div className="lv-split">
        <div className="min-w-0">
          <Head eyebrow="Smart Web Link" title="One Link. Everything About Your Business."
                lead="Turn your Instagram bio, WhatsApp profile or QR code into one smart destination for your customers." />
          <p className="lv-body mt-5">
            A business doesn't need multiple disconnected links. One Smart Web Link holds your WhatsApp, products,
            offers, reviews, directions, videos and enquiries — and every Smart QR and Smart Screen can point to it.
          </p>
          <div className="mt-8">
            <button type="button" className="lv-btn lv-btn--primary lv-btn--lg" onClick={onGetLinkola} data-testid="biz-link-cta">
              Create Your Smart Link <ArrowUpRight className="w-5 h-5" aria-hidden />
            </button>
          </div>
        </div>
        <div className="relative min-w-0 py-6" data-testid="biz-link-visual">
          {/* v149.80 — Realistic shop backdrop so the Smart Web Link
              phone mock reads as "this is what customers see when they
              scan from the counter". Soft overlays preserve the brutal
              phone frame + glowing orbs. */}
          <span aria-hidden className="absolute inset-x-0 top-0 bottom-0 rounded-3xl overflow-hidden pointer-events-none"
                style={{ zIndex: 0 }}>
            <img src="/landing/venue-cafe.webp" alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-45" />
            <span className="absolute inset-0" style={{ background: "radial-gradient(ellipse at 50% 60%, rgba(255,255,255,0.0) 0%, rgba(255,255,255,0.55) 70%, rgba(255,255,255,0.9) 100%)" }} />
          </span>
          <span className="lv-glow lv-glow--green" style={{ width: 260, height: 260, left: "10%", top: "10%" }} aria-hidden />
          <span className="lv-glow lv-glow--coral" style={{ width: 220, height: 220, right: "8%", bottom: "6%" }} aria-hidden />
          <div className="relative" style={{ maxWidth: 320, margin: "0 auto" }}>
            <PhoneList title="Café Aroma" rows={LINK_ROWS}
                       label="A phone showing a Smart Web Link profile: avatar, name, bio, social icons, then buttons for WhatsApp, products and catalogue, offers, Google Reviews, directions, social media, videos and enquiries" />
          </div>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 6 · Repeat customers & loyalty (biz_hq_view)
// ---------------------------------------------------------------------------
const JOURNEY = [
  { icon: MapPin, title: "Discover" }, { icon: Store, title: "Visit" }, { icon: Heart, title: "Engage" },
  { icon: Gift, title: "Reward" }, { icon: Repeat, title: "Return" },
];
function Loyalty() {
  return (
    <Section id="loyalty" testid="biz-loyalty" tone="light">
      <div className="lv-split">
        <div className="min-w-0">
          <Head eyebrow="Repeat customers" title="Turn First-Time Customers Into Regulars." />
          <div className="mt-8">
            <Points items={[
              { icon: Repeat, text: "Reward repeat visits" }, { icon: Gift, text: "Offer loyalty incentives" },
              { icon: ShoppingBag, text: "Encourage return purchases" }, { icon: BarChart3, text: "Track customer engagement" },
              { icon: Users, text: "Understand repeat customer behaviour" },
            ]} />
          </div>
        </div>
        <ol className="lv-flow justify-center" data-testid="biz-loyalty-flow" aria-label="Discover, visit, engage, reward, return">
          {JOURNEY.map((s, i) => {
            const Icon = s.icon;
            return (
              <li key={s.title} className="contents">
                <div className="lv-card flex flex-col items-center gap-2 text-center" style={{ flex: "1 1 7.5rem", padding: "1rem 0.75rem" }}>
                  <span className={`lv-icon ${i === JOURNEY.length - 1 ? "lv-icon--coral" : "lv-icon--green"}`}><Icon className="w-5 h-5" aria-hidden /></span>
                  <span className="lv-display text-[14px] uppercase">{s.title}</span>
                </div>
                {i < JOURNEY.length - 1 && <span className="lv-flow__arrow" aria-hidden><ArrowRight className="w-4 h-4" /></span>}
              </li>
            );
          })}
        </ol>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 7 · Broadcast & engagement (biz_repeat_patterns)
// ---------------------------------------------------------------------------
const ENGAGE_STEPS = [
  { icon: Users, title: "Customer audience selection" }, { icon: Megaphone, title: "Campaign creation" },
  { icon: Send, title: "Broadcast communication" }, { icon: BarChart3, title: "Engagement reporting" },
];
const ENGAGE_RESULTS = [["Sent", 100], ["Engaged", 62], ["Offer used", 24]];
function Engagement() {
  return (
    <Section id="engagement" testid="biz-engagement" tone="dark">
      <div className="lv-split lv-split--flip">
        <div className="min-w-0">
          <Head eyebrow="Broadcast & engagement" title="Stay Connected. Stay Remembered."
                lead="Engage opted-in customers with offers, announcements and updates through the channels Linkola supports." />
          <ol className="grid gap-3 mt-8" data-testid="biz-engagement-steps">
            {ENGAGE_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <li key={s.title} className="flex items-center gap-3 min-w-0">
                  <span className="lv-icon" style={{ width: 36, height: 36, borderRadius: 10, ...GREEN_ICON }}><Icon className="w-4 h-4" aria-hidden /></span>
                  <span className="lv-mono" style={{ color: "var(--lv-slate)" }}>0{i + 1}</span>
                  <span className="min-w-0 break-words font-semibold text-[15px]">{s.title}</span>
                </li>
              );
            })}
          </ol>
        </div>
        <div className="lv-card lv-card--dark min-w-0" data-testid="biz-engagement-mock">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-bold text-[15px]">Broadcast</p>
            <span className="ml-auto"><DemoBadge testid="biz-engagement-demo" /></span>
          </div>
          <p className="lv-mono mt-4 mb-2" style={{ color: "var(--lv-slate)" }}>Audience</p>
          <div className="flex flex-wrap gap-2">
            {["Opted-in customers", "Visited in 30 days", "Loyalty members"].map((a, i) => (
              <span key={a} className={`lv-chip ${i === 0 ? "lv-chip--green" : "lv-chip--dark"}`}>{a}</span>
            ))}
          </div>
          <p className="lv-mono mt-5 mb-2" style={{ color: "var(--lv-slate)" }}>Campaign</p>
          <div className="lv-kpi flex items-start gap-3">
            <span className="lv-icon" style={{ width: 36, height: 36, borderRadius: 10, background: "var(--lv-coral)", color: "var(--lv-surface)" }}><Tag className="w-4 h-4" aria-hidden /></span>
            <span className="min-w-0">
              <span className="block font-bold text-[15px] break-words">Weekend special — new matcha latte</span>
              <span className="block lv-body" style={{ fontSize: 13 }}>Offer · valid Sat–Sun · show at the counter</span>
            </span>
          </div>
          <p className="lv-mono mt-5 mb-2" style={{ color: "var(--lv-slate)" }}>Results</p>
          <ul className="grid gap-2.5">
            {ENGAGE_RESULTS.map(([k, v]) => (
              <li key={k} className="grid grid-cols-[6rem_1fr_2.5rem] items-center gap-3">
                <span className="text-[13px] font-semibold truncate">{k}</span>
                <span className="lv-bar"><span style={{ width: `${v}%` }} /></span>
                <span className="text-[13px] font-bold text-right tabular-nums">{v}%</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 8 · Multi-branch (biz_central_engagement)
// ---------------------------------------------------------------------------
const BRANCH_TABS = ["All", "Branch A", "Branch B", "Branch C"];
const BRANCH_KPIS = {
  "All": [["Customers", "12,400"], ["Repeat rate", "36%"], ["QR scans", "24,900"], ["Offers used", "2,180"]],
  "Branch A": [["Customers", "4,820"], ["Repeat rate", "38%"], ["QR scans", "8,420"], ["Offers used", "740"]],
  "Branch B": [["Customers", "2,940"], ["Repeat rate", "31%"], ["QR scans", "4,830"], ["Offers used", "420"]],
  "Branch C": [["Customers", "4,640"], ["Repeat rate", "37%"], ["QR scans", "11,650"], ["Offers used", "1,020"]],
};
const INDUSTRIES = ["Retail chains", "Restaurants", "Salons", "Clinics", "Franchises", "Multi-location businesses"];
function MultiBranch({ onTalk }) {
  const [tab, setTab] = useState("All");
  return (
    <Section id="multi-branch" testid="biz-multi-branch" tone="light">
      <Head center eyebrow="Multi-branch" title="One Shop or a Hundred. One Linkola."
            lead="Linkola supports individual businesses and multi-location operations." />
      <div className="lv-split mt-12">
        <div className="lv-card lv-card--raised min-w-0" data-testid="biz-multi-branch-dashboard">
          <div className="flex flex-wrap items-center gap-2">
            <Building2 className="w-4 h-4 lv-accent" aria-hidden />
            <p className="font-bold text-[15px]">HQ overview</p>
            <span className="ml-auto"><DemoBadge testid="biz-multi-branch-demo" /></span>
          </div>
          <div className="flex gap-2 overflow-x-auto overscroll-contain mt-4 pb-1 -mx-1 px-1" role="tablist" aria-label="Branch" data-testid="biz-multi-branch-tabs">
            {BRANCH_TABS.map((t) => (
              <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)}
                      className={`lv-chip flex-shrink-0 min-h-[40px] ${tab === t ? "lv-chip--dark" : ""}`}
                      data-testid={`biz-multi-branch-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}>
                {t}
              </button>
            ))}
          </div>
          <ul className="grid grid-cols-2 gap-2 mt-4">
            {BRANCH_KPIS[tab].map(([k, v]) => (
              <li key={k} className="lv-kpi">
                <p className="lv-kpi__label">{k}</p>
                <p className="lv-kpi__value" data-testid={`biz-multi-branch-kpi-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</p>
              </li>
            ))}
          </ul>
          <p className="lv-body mt-3" style={{ fontSize: 12 }}>Last 30 days · {tab === "All" ? "all branches" : tab} · tap a branch to drill down.</p>
        </div>
        <div className="min-w-0">
          <Points items={[
            { text: "HQ overview" }, { text: "Branch-level insights" }, { text: "Customer engagement by branch" },
            { text: "Loyalty and offers" }, { text: "Centralized campaign management" }, { text: "Local execution" },
          ]} />
          <div className="flex flex-wrap gap-2 mt-7" data-testid="biz-multi-branch-industries">
            {INDUSTRIES.map((w) => <span key={w} className="lv-chip">{w}</span>)}
          </div>
          <div className="mt-8">
            <button type="button" className="lv-btn lv-btn--ghost" onClick={() => onTalk("biz_multi_branch")} data-testid="biz-multi-branch-talk">
              Talk to us <ArrowRight className="w-4 h-4" aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 9 · Nearby promotion — one story, two channels (biz_central_broadcast)
// ---------------------------------------------------------------------------
function Nearby({ onGetLinkola }) {
  return (
    <Section id="nearby" testid="biz-nearby" tone="soft">
      <Head center eyebrow="Nearby promotion" title="Choose where your business gets discovered." />
      <div className="lv-grid-2 mt-12 max-w-4xl mx-auto">
        <div className="lv-card lv-card--raised flex flex-col" data-testid="biz-nearby-qr">
          <span className="lv-icon lv-icon--coral"><QrCode className="w-5 h-5" aria-hidden /></span>
          <h3 className="lv-h3 mt-5">Smart QR</h3>
          <p className="lv-body mt-2 flex-1">Digital promotions through nearby QR touchpoints.</p>
          <button type="button" className="lv-btn lv-btn--primary mt-6 self-start" onClick={onGetLinkola} data-testid="biz-nearby-qr-cta">Get Started</button>
        </div>
        <div className="lv-card lv-card--dark flex flex-col" data-testid="biz-nearby-screens">
          <span className="lv-icon lv-icon--green"><MonitorPlay className="w-5 h-5" aria-hidden /></span>
          <h3 className="lv-h3 mt-5">Smart Screens</h3>
          <p className="lv-body mt-2 flex-1" style={{ color: "var(--lv-slate)" }}>Visual advertisements on physical digital displays, published by the Linkola team.</p>
          <a href={SCREENS_URL} target="_blank" rel="noopener noreferrer" className="lv-btn lv-btn--green mt-6 self-start" data-testid="biz-nearby-screens-cta">
            Explore Smart Screens <ExternalLink className="w-4 h-4" aria-hidden />
          </a>
        </div>
      </div>
      <p className="lv-body text-center mt-8 max-w-2xl mx-auto">Two distinct channels, each with its own creatives and reporting.</p>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 10 · How Linkola works (biz_central_promotions)
// ---------------------------------------------------------------------------
const HOW = [
  { icon: Link2, title: "Create your Smart Web Link", text: "Put everything about your business behind one link." },
  { icon: QrCode, title: "Add Smart QR touchpoints", text: "On tables, counters, packaging and print." },
  { icon: MonitorPlay, title: "Promote nearby", text: "Through Smart QR or Smart Screens — your choice." },
  { icon: Repeat, title: "Reward and re-engage customers", text: "Loyalty, offers and broadcasts bring them back." },
];
function HowItWorks({ user, onGetLinkola, onOpenApp }) {
  return (
    <Section id="how-it-works" testid="biz-how-it-works" tone="light">
      <Head center eyebrow="How Linkola works" title="Simple to start. Built to grow." />
      <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mt-12" data-testid="biz-how-steps">
        {HOW.map((s, i) => {
          const Icon = s.icon;
          return (
            <li key={s.title} className="lv-card min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="lv-icon lv-icon--green"><Icon className="w-5 h-5" aria-hidden /></span>
                <span className="lv-display" style={{ fontSize: "2rem", color: "var(--lv-border)" }} aria-hidden>0{i + 1}</span>
              </div>
              <h3 className="lv-h3 mt-5" style={{ fontSize: "1.15rem" }}><span className="sr-only">Step {i + 1}: </span>{s.title}</h3>
              <p className="lv-body mt-2" style={{ fontSize: 14 }}>{s.text}</p>
            </li>
          );
        })}
      </ol>
      <div className="mt-10 flex justify-center">
        <button type="button" className="lv-btn lv-btn--primary lv-btn--lg" onClick={user ? onOpenApp : onGetLinkola} data-testid="biz-how-cta">
          {user ? "Open app" : "Get Started"} <ArrowUpRight className="w-5 h-5" aria-hidden />
        </button>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 11 · Built for every local business (biz_branch_compare)
// ---------------------------------------------------------------------------
const TICKER = ["Retail chains", "Restaurants & cafés", "Salons & spas", "Clinics", "Gyms", "Supermarkets", "Franchises", "Hotels", "Real estate", "Local services"];
function BuiltFor() {
  return (
    <section id="for-business" data-testid="biz-for-business" className="lv-section--dark scroll-mt-20 overflow-hidden py-10 sm:py-14">
      <div className="lv-container text-center">
        <p className="lv-eyebrow mb-4">Built for every local business</p>
        <h2 className="lv-h3 max-w-2xl mx-auto">If customers walk through your door, Linkola is built for you.</h2>
      </div>
      <div className="lv-ticker mt-8" data-testid="biz-for-business-ticker" aria-label={TICKER.join(", ")}>
        <div className="lv-ticker__track" aria-hidden>
          {[...TICKER, ...TICKER].map((t, i) => <span key={`${t}-${i}`}>{t}</span>)}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// 12 · Pricing (biz_plans) — plans from GET /plans, nothing hard-coded
// ---------------------------------------------------------------------------
function fmtMoney(n, cur) {
  const v = Number(n) || 0;
  if (cur === "USD") return `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return `₹${Math.round(v).toLocaleString("en-IN")}`;
}
const cycleOf = (p) => p.billing_cycle || (p.interval_days === 365 ? "yearly" : p.interval_days === 30 ? "monthly" : "custom");
const tierOf = (p) => p.tier || String(p.plan_id || "").replace(/[-_](monthly|yearly)$/, "");
const slug = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-");

function PlanCard({ plan, free = false, onGetLinkola }) {
  const cur = plan.display_currency || "INR";
  const price = Number(plan.display_price) || 0;
  const mrp = Number(plan.display_mrp) || 0;
  const cycle = cycleOf(plan);
  const unavailable = plan.available === false;
  return (
    <article className={`lv-card ${free ? "" : "lv-card--raised"} flex flex-col min-w-0`} data-testid={`biz-pricing-plan-${slug(tierOf(plan))}`}>
      <p className="lv-mono" style={{ color: "var(--lv-ink-3)" }}>{free ? "Free" : cycle === "yearly" ? "Billed yearly" : cycle === "monthly" ? "Billed monthly" : "Plan"}</p>
      <h3 className="lv-h3 mt-2 break-words">{plan.name || tierOf(plan)}</h3>
      <div className="mt-4 flex items-baseline gap-2 flex-wrap min-w-0">
        {unavailable ? (
          <span className="font-bold lv-body">Not available in {cur}</span>
        ) : (
          <>
            {mrp > price && <span className="line-through lv-body font-semibold" data-testid="biz-pricing-mrp">{fmtMoney(mrp, cur)}</span>}
            <span className="lv-display" style={{ fontSize: "2.2rem" }}>{free || price === 0 ? "Free" : fmtMoney(price, cur)}</span>
            {!free && price > 0 && <span className="lv-body font-semibold">{cycle === "yearly" ? "/year" : cycle === "monthly" ? "/month" : ""}</span>}
          </>
        )}
      </div>
      <ul className="grid gap-2 mt-5 flex-1">
        {(plan.features || []).slice(0, 6).map((f) => (
          <li key={f} className="flex items-start gap-2 text-[14px] min-w-0">
            <Check className="w-4 h-4 mt-0.5 flex-shrink-0 lv-accent" strokeWidth={2.6} aria-hidden />
            <span className="min-w-0 break-words">{f}</span>
          </li>
        ))}
      </ul>
      <button type="button" className={`lv-btn ${free ? "lv-btn--ghost" : "lv-btn--primary"} w-full mt-6`} onClick={onGetLinkola}
              disabled={unavailable} style={unavailable ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
              data-testid={`biz-pricing-cta-${slug(tierOf(plan))}`}>
        {free ? "Start free" : `Choose ${plan.name || tierOf(plan)}`}
      </button>
    </article>
  );
}

function Pricing({ onGetLinkola, onTalk }) {
  const [plans, setPlans] = useState(null);
  const [error, setError] = useState(false);
  const [cycle, setCycle] = useState("yearly");
  useEffect(() => {
    let alive = true;
    api.get("/plans")
      .then((r) => { if (alive) setPlans(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, []);

  const ah = (plans || []).filter((p) => p.family === "action_hub" && p.cta !== "contact_sales");
  const free = ah.find((p) => !(Number(p.display_price) > 0));
  const tiers = [];
  for (const p of ah.filter((x) => Number(x.display_price) > 0)) {
    const t = tierOf(p);
    const g = tiers.find((x) => x.tier === t) || tiers[tiers.push({ tier: t, variants: [] }) - 1];
    g.variants.push(p);
  }
  const shown = tiers.map((g) => g.variants.find((v) => cycleOf(v) === cycle) || g.variants[0]);
  const hasBoth = tiers.some((g) => g.variants.some((v) => cycleOf(v) === "monthly") && g.variants.some((v) => cycleOf(v) === "yearly"));
  const nothing = error || (plans && !free && shown.length === 0);
  const notes = [
    { title: "Smart QR", text: "Priced separately — plans and printed stands.", cta: { label: "Get Started", onClick: onGetLinkola, testid: "biz-pricing-note-qr" } },
    { title: "Smart Screens", text: "Advertising and screen hosting have their own commercial terms.", link: { label: "See linkola.au", testid: "biz-pricing-note-screens" } },
    { title: "Multi-branch", text: "Pricing for HQ + branches is agreed with you.", cta: { label: "Talk to us", onClick: () => onTalk("biz_pricing"), testid: "biz-pricing-talk" } },
  ];

  return (
    <Section id="pricing" testid="biz-pricing" tone="light">
      <Head center eyebrow="Pricing" title="One clear plan for your Smart Web Link." />
      {hasBoth && (
        <div className="mt-8 flex justify-center">
          <div className="inline-flex rounded-full p-1" style={{ background: "var(--lv-surface-2)" }} role="group" aria-label="Billing cycle">
            {["monthly", "yearly"].map((c) => (
              <button key={c} type="button" aria-pressed={cycle === c} onClick={() => setCycle(c)}
                      className={`lv-btn lv-btn--sm ${cycle === c ? "lv-btn--primary" : ""}`} style={cycle === c ? undefined : { color: "var(--lv-ink-2)" }}
                      data-testid={`biz-pricing-cycle-${c}`}>
                {c === "monthly" ? "Monthly" : "Yearly"}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="mt-10">
        {nothing ? (
          <div className="lv-card lv-card--raised max-w-md mx-auto text-center" data-testid="biz-pricing-error">
            <p className="font-bold text-[16px]">See plans inside the app</p>
            <p className="lv-body mt-1" style={{ fontSize: 14 }}>Plans and prices are shown on the Upgrade screen after you sign in.</p>
            <button type="button" className="lv-btn lv-btn--primary mt-5" onClick={onGetLinkola} data-testid="biz-pricing-fallback-cta">Get Started</button>
          </div>
        ) : !plans ? (
          <div className="lv-grid-3" data-testid="biz-pricing-loading" aria-busy="true" aria-label="Loading plans">
            {[0, 1, 2].map((i) => (
              <div key={i} className="lv-card animate-pulse grid gap-3">
                <div className="lv-kpi h-5 w-1/3" /><div className="lv-kpi h-10 w-2/3" /><div className="lv-kpi h-4" /><div className="lv-kpi h-4" /><div className="lv-kpi h-12 mt-3" />
              </div>
            ))}
          </div>
        ) : (
          <div className="lv-grid-3" data-testid="biz-pricing-grid">
            {free && <PlanCard plan={free} free onGetLinkola={onGetLinkola} />}
            {shown.map((p) => <PlanCard key={p.plan_id} plan={p} onGetLinkola={onGetLinkola} />)}
          </div>
        )}
      </div>
      <div className="lv-grid-3 mt-6" data-testid="biz-pricing-notes">
        {notes.map((n) => (
          <div key={n.title} className="lv-card flex flex-col min-w-0" style={{ boxShadow: "none" }}>
            <p className="font-bold text-[15px]">{n.title}</p>
            <p className="lv-body mt-1 flex-1" style={{ fontSize: 14 }}>{n.text}</p>
            {n.cta && (
              <button type="button" className="lv-link mt-3 inline-flex items-center gap-1 self-start min-h-[44px]" onClick={n.cta.onClick} data-testid={n.cta.testid}>
                {n.cta.label} <ArrowRight className="w-4 h-4" aria-hidden />
              </button>
            )}
            {n.link && (
              <a href={SCREENS_URL} target="_blank" rel="noopener noreferrer" className="lv-link mt-3 inline-flex items-center gap-1 self-start min-h-[44px]" data-testid={n.link.testid}>
                {n.link.label} <ExternalLink className="w-4 h-4" aria-hidden />
              </a>
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 text-center"><Terms testid="biz-pricing-terms-link" /></div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// 13 · Final call to action (biz_final_cta)
// ---------------------------------------------------------------------------
function FinalCta({ user, onGetLinkola, onOpenApp, onTalk }) {
  return (
    <Section id="get-started" testid="biz-final-cta" tone="dark" className="lv-grid-bg">
      <div className="lv-glow lv-glow--green" style={{ width: 380, height: 380, top: -100, right: -100 }} aria-hidden />
      <div className="relative text-center max-w-3xl mx-auto min-w-0">
        <h2 className="lv-h2">Ready to connect more customers?</h2>
        <p className="lv-lead mt-5 mx-auto">Start with one Smart Web Link today, and grow into QR, screens and loyalty when you're ready.</p>
        <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
          <button type="button" className="lv-btn lv-btn--green lv-btn--lg" onClick={user ? onOpenApp : onGetLinkola} data-testid="biz-final-cta-btn">
            {user ? "Open app" : "Get Started"} <ArrowUpRight className="w-5 h-5" aria-hidden />
          </button>
          <button type="button" className="lv-btn lv-btn--ghost-dark lv-btn--lg" onClick={() => onTalk("biz_final_cta")} data-testid="biz-final-talk">
            Talk to us
          </button>
        </div>
        <div className="mt-10 grid gap-1.5 justify-items-center">
          <p className="font-semibold text-[15px] sm:text-base" data-testid="biz-final-guarantee">
            Increase your repeat customers by 10% in 3 months — or get your money back.<span className="lv-accent-green">*</span>
          </p>
          <Terms />
        </div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// The page — keys and order frozen in lib/landingSections.js
// ---------------------------------------------------------------------------
const RENDER = {
  biz_hero: Hero,
  biz_outcomes: Capabilities,
  biz_journey: SmartScreens,
  biz_actions: SmartQr,
  biz_multi_branch: SmartLink,
  biz_hq_view: Loyalty,
  biz_repeat_patterns: Engagement,
  biz_central_engagement: MultiBranch,
  biz_central_broadcast: Nearby,
  biz_central_promotions: HowItWorks,
  biz_branch_compare: BuiltFor,
  biz_nearby: null, // merged into Nearby (biz_central_broadcast)
  biz_plans: Pricing,
  biz_pricing: null, // merged into Pricing (biz_plans)
  biz_final_cta: FinalCta,
};

export default function BusinessLanding({ order, sections, user, onGetLinkola, onOpenApp, onTalk }) {
  return (
    <div data-testid="biz-landing">
      {order.filter((key) => sections[key] && RENDER[key]).map((key) => {
        const Comp = RENDER[key];
        return <Comp key={key} sections={sections} user={user} onGetLinkola={onGetLinkola} onOpenApp={onOpenApp} onTalk={onTalk} />;
      })}
    </div>
  );
}
